import { isBefore } from 'date-fns';
import React, { useMemo } from 'react';

import { Button, Icon, Space, Typography } from '@sravni/react-design-system';
import { Message } from '@sravni/react-icons';
import { PAGE_TYPE } from '@sravni/ugc/lib/organizations/constants';

import { getAnalyticsObject } from '../../../../helpers/analyticsEvents';
import { getReviewUrl } from '../../../../helpers/reviews';
import { AuthorInfo } from '../../../AuthorInfo';
import { Likes } from '../../../Likes';
import { TReviewCardProps } from '../../../ReviewCard';
import { BadgeBlock } from '../../../ReviewCard/components/BadgeBlock';
import { RateStatus } from '../../../ReviewCard/components/RateStatus';
import { ReviewBody } from '../../../ReviewCard/components/ReviewBody';
import { ReviewInfo } from '../../../ReviewCard/components/ReviewInfo';
import { getStatusBadgeInfo } from '../../../ReviewCard/helpers/common';
import styles from '../../../ReviewCard/review-card.module.scss';

export type TListReviewCardProps = Pick<TReviewCardProps, 'tags' | 'type' | 'reviewTypes' | 'review' | 'isMobile'> & {};

export const ListCard: React.FC<TListReviewCardProps> = ({ reviewTypes, review, tags, type, isMobile }) => {
  const commentCount = review.commentsCount;
  const isOld = isBefore(new Date(review.date), new Date(2013, 1, 1));
  const resolved = review.problemSolved && !isOld;

  const statusBadgeInfo = getStatusBadgeInfo({ isResolved: resolved, ratingStatus: review.ratingStatus });
  const gaObj = useMemo(() => getAnalyticsObject(review.organizationName, type as PAGE_TYPE, null, review.id), [
    review.organizationName,
    type,
    review.id,
  ]);
  const reviewUrl = getReviewUrl({
    pageType: type,
    typeAlias: reviewTypes.itemTypeUrl,
    organizationAlias: review.alias,
    reviewId: review.id,
  });

  const reviewBody = (
    <a
      key={review.id}
      href={reviewUrl?.as || review.externalUrl}
      className={styles.link}
      target={'_blank'}
      rel={'noreferrer'}
    >
      <ReviewBody review={review} isMobile={isMobile} isList />
    </a>
  );

  return (
    <div data-id={review.id}>
      <div className={styles.wrapper}>
        <Space justify={'space-between'} align={'start'} className={'h-mb-20'}>
          <AuthorInfo entity={review} isList showAvatar={false} />
          <RateStatus review={review} />
        </Space>

        {review.isExternal ? <noindex>{reviewBody}</noindex> : reviewBody}

        <ReviewInfo reviewTypes={reviewTypes} type={type} review={review} isMobile={isMobile} tags={tags} isList />

        <Space
          justify={'space-between'}
          align={'center'}
          direction={isMobile ? 'vertical' : 'horizontal'}
          className={'h-mt-20'}
        >
          <Space size={8}>
            {review.hasCompanyResponse && (
              <a href={`${reviewUrl?.as}#companyAnswer`}>
                <BadgeBlock icon={<Message />} text={'официальный ответ'} />
              </a>
            )}
            {statusBadgeInfo && <BadgeBlock icon={statusBadgeInfo.icon} text={statusBadgeInfo.text} />}
          </Space>

          <Space justify={isMobile ? 'start' : 'end'} align={'center'} size={8} className={isMobile && 'h-mt-20'}>
            {review.isExternal ? (
              <a href={review.externalUrl} target={'_blank'} rel={'nofollow noopener noreferrer'}>
                Читать на {review.externalSource}
              </a>
            ) : (
              <>
                <Space justify={isMobile ? 'space-between' : 'end'} align={'center'}>
                  <Space size={8} justify={'end'} align={'center'}>
                    {!isMobile && (
                      <Typography.Text strong nowrap>
                        Помог ли отзыв?
                      </Typography.Text>
                    )}

                    <Likes reviewId={review.id} votes={review.votes} isUserVoted={review.vote} analyticLabel={gaObj} />

                    <a href={`${reviewUrl?.as}#comments`}>
                      <Button size={36} variant="outlined" className={styles.commentButton}>
                        {!!commentCount && commentCount} <Icon size={20} icon={<Message />} />
                      </Button>
                    </a>
                  </Space>
                </Space>
              </>
            )}
          </Space>
        </Space>
      </div>
    </div>
  );
};
